// Updated PublicCompanyProfile.jsx from copied CompanyProfile.jsx

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import DOMPurify from 'dompurify';

function decodeEntities(encodedString) {
  const textarea = document.createElement('textarea');
  textarea.innerHTML = encodedString;
  return textarea.value;
}

const PublicCompanyProfile = () => {
  const website = useParams()['*']; // splat captures full path for franchise URLs
  const [company, setCompany] = useState(null);
  const [users, setUsers] = useState([]);
  const navigate = useNavigate();

  const [reviews] = useState({
    average: 4.8,
    total: 127,
    distribution: [85, 20, 15, 5, 2],
    recent: [
      { id: 1, name: 'John D.', rating: 5, comment: 'Great service! Very professional and completed the job quickly.', date: '2 days ago' },
      { id: 2, name: 'Sarah M.', rating: 5, comment: 'Excellent work and communication throughout the project.', date: '1 week ago' },
      { id: 3, name: 'Mike R.', rating: 4, comment: 'Very satisfied with the results. Would recommend.', date: '2 weeks ago' },
    ]
  });

  useEffect(() => {
    const fetchData = async () => {
      if (!website) return;
      try {
        const companyResponse = await axios.get(`/api/company/${encodeURIComponent(website)}`);
        setCompany(companyResponse.data);

        const usersResponse = await axios.get(`/api/business/${companyResponse.data.b_id}/users`);

        if (Array.isArray(usersResponse.data) && usersResponse.data.length > 0) {
          setUsers(usersResponse.data);
        }
      } catch (error) {
        console.error('Error fetching company data:', error);
        setCompany(null);
      }
    };

    fetchData();
  }, [website]);

  const handleUserClick = (associate) => {
    const userSlug = `${associate.u_first_name.toLowerCase()}_${associate.u_last_name.toLowerCase()}`;
    navigate(`/userprofiles/users/${userSlug}`);
  };

  if (!company) return <div className="p-4 text-center text-gray-500">Loading...</div>;

  return (
    <div className="container mx-auto w-4/5 pt-6 pb-6">
      <div className="flex flex-col md:flex-row gap-3">

        {/* Business Info */}
        <div className="w-full md:w-[48%] bg-white rounded-lg p-4 shadow-[0_0_20px_rgba(0,0,150,0.1)] flex flex-col">
          <h1 className="text-2xl font-bold break-words text-[#00aef7] leading-tight mb-1">
            {company.b_name}
          </h1>

          <div className="flex justify-between items-start -mt-2">
            <div className="flex-1 pt-1.5">
              <div style={{ minHeight: '60px' }}>
                {company.b_address && (
                  <p className="text-sm text-gray-600 leading-none">{company.b_address}</p>
                )}

                {(company.b_city || company.b_state || company.b_zip_code) && (
                <p className="text-sm text-gray-600 leading-none">
                    {company.b_city && `${company.b_city}, `}{company.b_state} {company.b_zip_code}
                  </p>
                )}
              </div>

              <p className="text-sm text-gray-600 leading-none">
                <a href={`tel:${company.b_phone}`} className="hover:text-blue-600">
                  {company.b_phone}
                </a>
              </p>
            {company && company.b_website && (
              <p className="text-sm text-gray-600 mt-1">
                <a
                  href={`https://${company.b_website.replace(/^https?:\/\//, '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#00aef7] hover:text-blue-600"
                >
                  Visit our Website
                </a>
              </p>
            )}

            </div>
            <div className="w-[54%] relative pt-4">
              <div className="relative w-full h-24 max-w-[280px] mx-auto group">
                {company.b_logo ? (
                  <img 
                    src={`${process.env.REACT_APP_API_URL}${company.b_logo}`} 
                    alt="Business Logo"
                    className="w-full h-full object-contain" 
                  />
                ) : (
                  <img 
                    src="/images/Logo_Placeholder.jpg"
                    alt="Logo Placeholder"
                    className="w-full h-full object-contain opacity-50 border-2 border-[#00aef7]/30 rounded-md"
                  />
                )}
              </div>

            </div>
          </div>

          <div className="text-center w-[54%] self-end -mt-8">
            <p className="text-lg font-bold text-gray-600">{company.b_business_industry}</p>
          </div>

          <div className="mt-3 flex-1 overflow-y-auto max-h-[300px] pr-2 relative">
        {company?.b_about_us && (() => {
          const raw = company.b_about_us;
          const decoded = decodeEntities(raw);
          const cleaned = DOMPurify.sanitize(decoded);

          console.log("🔥 Raw HTML:", raw);
          console.log("📜 Decoded HTML:", decoded);
          console.log("🧼 Cleaned HTML:", cleaned);

          return (
            <div
              className="w-full h-full p-2 pt-8 text-gray-700 bg-white ql-editor"
              dangerouslySetInnerHTML={{ __html: cleaned }}
            />
          );
        })()}

          </div>
        </div>

        {/* Associates Column */}
        <div className="w-full md:w-[24%] bg-white rounded-lg p-4 shadow-[0_0_20px_rgba(0,0,150,0.1)]">
          <h2 className="text-xl font-bold text-[#00aef7] mb-4 text-center">Associates</h2>
          <div className="grid grid-cols-1 gap-4">
            {users.map((associate) => (
              <div key={associate.u_id} className="bg-gray-50 rounded-lg p-4 pt-3 relative cursor-pointer hover:shadow-lg transition-shadow flex flex-col items-center"
                onClick={() => handleUserClick(associate)}>
                <img
                  src={associate.u_profile_picture ? `${process.env.REACT_APP_API_URL}${associate.u_profile_picture}` : '/default-avatar.png'}
                  alt={`${associate.u_first_name} ${associate.u_last_name}`}
                  className="w-20 h-20 rounded-full object-cover"
                />
              <div className="text-center space-y-1 mt-2">
                <h3 className="font-semibold">{associate.u_first_name} {associate.u_last_name}</h3>
                <p className="text-sm text-gray-600">{associate.u_job_title}</p>
                <p className="text-xs text-gray-500">
                  <a href={`tel:${associate.u_phone_number}`} className="hover:text-blue-600">{associate.u_phone_number}</a>
                </p>
                <div className="text-xs text-gray-500">
                  <a href={`mailto:${associate.u_email}`} className="hover:text-blue-600">{associate.u_email}</a>
                </div>
              </div>

              </div>
            ))}
          </div>
        </div>

        {/* Reviews */}
        <div className="w-full md:w-[28%] bg-white rounded-lg p-4 shadow-[0_0_20px_rgba(0,0,150,0.1)]">
          <h2 className="text-xl font-bold text-[#00aef7] mb-2 text-center">Reviews & Ratings</h2>



        </div>
      </div>
    </div>
  );
};

export default PublicCompanyProfile;
